import { isTwyne } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Twyne — a CREDIT DELEGATION layer over Aave V3 and Euler V2, and the only
 * lender we carry where the generic builder is wrong about four separate things
 * at once. It is not a pool, and almost nothing about it is derivable from the
 * flat market row:
 *
 *  1. **There is no market LTV — only a BAND.** Every position picks its own
 *     `twyneLiqLTV` between `externalLiqLtv x buffer` and `maxTwyneLTVs`, can
 *     change it at any time, and the EFFECTIVE threshold is clamped further at
 *     runtime when the credit vault runs short. The row publishes the FLOOR, so
 *     a sheet that presents it as "the" liquidation threshold understates by up
 *     to 5 percentage points — which is the entire product.
 *  2. **The borrower pays TWO interest legs on DIFFERENT bases.** The external
 *     market's borrow APR on the debt, PLUS the credit vault's rate on the
 *     RESERVED CREDIT, which is denominated in the COLLATERAL asset. The second
 *     one has no market-level value at all — its contribution is
 *     `creditApr x (reservedCredit / debt)`, per position — so it can never be
 *     folded into the headline rate. The sheet has to name it instead.
 *  3. **Liquidation is BY INHERITANCE.** The liquidator calls `liquidate()`,
 *     BECOMES the vault's borrower, and must make the position healthy before
 *     the same EVC batch ends. There is no seize-and-sell and no penalty
 *     auction, and below `beta x externalLiqLtv` the borrower keeps their FULL
 *     equity — the incentive only grows as the position deteriorates. Every
 *     word of the generic "your collateral is sold at a discount" copy is
 *     wrong here.
 *  4. **There is a SECOND liquidation, on the external protocol.** If Twyne's
 *     liquidators do not act in time, Aave or Euler liquidates the joint
 *     position at ITS threshold and with ITS incentive — and after that the
 *     vault reverts every function except `handleExternalLiquidation()`. That
 *     is a state in which a position cannot be repaid, topped up or closed at
 *     all, which no `availability` flag on any other lender has to express.
 *
 * Plus, on the PT markets, a maturity: the collateral is a Pendle principal
 * token, so the position has a clock even though the loan does not.
 */

const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined

const pct = (v: number) => `${(v * 100).toFixed(2)} %`

export const twyneAdapter: TermAdapter = {
  id: 'twyne',
  matches: isTwyne,
  profileId: () => 'twyne.credit-delegation@v1',
  build: (input) => {
    const tw = (input.market?.twyne ?? {}) as Record<string, unknown>

    const floorLtv = num(tw.floorLtv)
    const maxLtv = num(tw.maxLtv)
    const externalLiqLtv = num(tw.externalLiqLtv)
    const creditApr = num(tw.creditApr)
    const maturity = num(tw.collateralMaturity)
    const matured = tw.collateralMatured === true
    const creditAvailable = num(tw.creditAvailable)
    const vaultType = typeof tw.vaultType === 'string' ? tw.vaultType : undefined
    const externalName = vaultType === 'AAVE_V3' ? 'Aave V3' : vaultType === 'EULER_V2' ? 'Euler V2' : 'the external market'

    // Reserving credit is what buys the extra LTV, so an empty credit vault
    // caps every new position at the FLOOR however healthy the market looks.
    // That is an availability fact, not a rate fact.
    const creditExhausted = creditAvailable != null && creditAvailable <= 0

    const bandNote =
      floorLtv != null && maxLtv != null
        ? `You choose this position's liquidation LTV anywhere between ${pct(floorLtv)} and ${pct(maxLtv)}, and you can change it later. The higher you set it, the more credit the position reserves and the more you pay for it.`
        : undefined

    return {
      constraints: {
        // The position is a CONTRACT the borrower deploys, and they may own
        // several per market — each with its own collateral, debt and LTV.
        positionModel: 'sub-account',
        positionIdMeaning:
          'the address of the collateral vault you deployed — one contract per ' +
          'position, and you can own several in the same market',
        crossMargin: false,
      },
      supply: {
        info: {
          description:
            'Credit-LPs deposit a receipt token they already hold (an Euler eToken, or ' +
            'a Twyne wrapper around an Aave aToken) and keep earning that token’s own ' +
            'yield, plus a delegation fee paid by borrowers who reserve their idle ' +
            'borrowing power.',
        },
        // Credit-LPs are FIRST-LOSS for the fallback-liquidation gap. Neither
        // of these is left to default: `overcollateralized` would read as a
        // guarantee that does not exist here, and `protected: true` would say
        // the principal cannot be impaired, which is exactly what the delegation
        // risk is.
        counterparty: { solvency: 'tranched-junior' },
        principal: { protected: false, risks: ['first-loss', 'bad-debt'] },
        exit: {
          partialAllowed: true,
        },
      },
      borrow: {
        debtShape: 'accruing',
        info: {
          description: [
            `Your collateral stays in ${externalName}; Twyne only reserves other lenders’ unused borrowing power so the same collateral supports a larger loan.`,
            bandNote,
            creditApr != null
              ? `On top of ${externalName}’s borrow rate you pay ${creditApr.toFixed(3)} % a year on the RESERVED CREDIT only — not on your debt — and it is charged in the collateral asset.`
              : undefined,
            // The loan is perpetual; the COLLATERAL is what expires. Said here
            // rather than in `maturity`, which would mis-type the loan itself.
            maturity != null && matured
              ? 'This market’s collateral has already matured. Existing positions can still be repaid and closed, but nothing new should be opened against it.'
              : maturity != null
                ? `Your loan has no end date, but the collateral is a fixed-maturity token that stops accreting on ${new Date(maturity * 1000).toISOString().slice(0, 10)}, and ${externalName}’s risk parameters move as that date approaches.`
                : undefined,
          ]
            .filter(Boolean)
            .join(' '),
        },
        availability: {
          // A matured PT still services existing positions but must not take
          // new ones, and an exhausted credit vault cannot grant extra LTV.
          ...(matured || creditExhausted ? { canOpen: false } : {}),
          canClose: true,
        },
        exit: {
          partialAllowed: true,
        },
        liquidation: {
          // Neither 'pool' nor an auction: the liquidator inherits the position.
          model: 'inheritance',
          trigger: 'price',
          penalties: [
            {
              id: 'twyne-inheritance',
              label: 'Twyne liquidation (by inheritance)',
              value: 0,
              description:
                'A liquidator takes over your vault and must make it healthy in the same ' +
                'transaction. Nothing is auctioned. While your LTV is still below the ' +
                'protocol’s safety line you keep your entire remaining equity; the ' +
                'liquidator’s cut grows from zero only as the position gets worse, and ' +
                'reaches all of your equity at the maximum LTV.',
            },
            {
              id: 'external-fallback',
              label: `${externalName} fallback liquidation`,
              value: 0,
              description:
                `If no Twyne liquidator acts in time, ${externalName} liquidates the whole ` +
                'position at its own threshold and its own penalty. After that the vault ' +
                'is frozen: it can only be wound up, not repaid, topped up or closed.',
            },
          ],
          ...(externalLiqLtv != null
            ? {
                description:
                  `Twyne liquidates first, above your chosen LTV. ${externalName}’s own ` +
                  `threshold of ${pct(externalLiqLtv)} is the backstop, and reaching it is the bad case.`,
              }
            : {}),
        },
        // THE LOAN IS PERPETUAL. Only the COLLATERAL expires.
        //
        // The first version of this adapter put the PT's expiry on
        // `borrow.maturity` with `kind: 'fixed-date'`, and that is wrong in a
        // way that shows up immediately in a UI: every consumer reads that field
        // as "this is a fixed-TERM loan", so the market rendered with a `Fixed`
        // borrow-rate badge and no APR — a fixed-rate product Twyne does not
        // offer. A Twyne loan has no end date, no rollover and no settlement;
        // it accrues at the external market's variable rate until repaid.
        //
        // What the collateral's expiry actually means is carried where it
        // belongs: `twyne.collateralMaturity` / `collateralMatured` on the
        // market descriptor (computed at read time), and stated in prose here.
        maturity: { kind: 'perpetual' },
      },
    }
  },
}
